/**
 * Application-wide locale provider.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { LOCALE_METADATA_V0, resolveSafeLocale, type SupportedLocale } from './localeCanon';
import {
  readBrowserLocaleV0,
  resolveLocalePrecedenceV0,
  type LocalePlaneV0,
} from './localePrecedence';
import {
  readSessionLocaleV0,
  readTenantDefaultLocaleV0,
  readUserPreferredLocaleV0,
  writeSessionLocaleV0,
  writeTenantDefaultLocaleV0,
  writeUserPreferredLocaleV0,
} from './preferenceStorage';
import { tV0, type TranslationParamsV0 } from './translate';
import type { RequiredTranslationKeyV0 } from './resources/catalog';

export interface LocaleContextValueV0 {
  locale: SupportedLocale;
  plane: LocalePlaneV0;
  source: string;
  setSessionLocale: (locale: SupportedLocale) => void;
  setUserPreferredLocale: (locale: SupportedLocale) => void;
  setTenantDefaultLocale: (locale: SupportedLocale) => void;
  t: (key: RequiredTranslationKeyV0, params?: TranslationParamsV0) => string;
  htmlLang: string;
  direction: 'ltr';
}

const LocaleContext = createContext<LocaleContextValueV0 | null>(null);

export interface LocaleProviderProps {
  children: React.ReactNode;
  plane?: LocalePlaneV0;
  tenantId?: string | null;
  /** Optional override from durable tenant.settings.language */
  tenantDefaultLocaleFromServer?: string | null;
  initialLocale?: SupportedLocale;
}

export function LocaleProvider({
  children,
  plane = 'business',
  tenantId = null,
  tenantDefaultLocaleFromServer = null,
  initialLocale,
}: LocaleProviderProps): React.ReactElement {
  const [sessionTick, setSessionTick] = useState(0);

  const resolved = useMemo(() => {
    void sessionTick;
    return resolveLocalePrecedenceV0({
      plane,
      sessionSelection: initialLocale ?? readSessionLocaleV0(),
      userPreferredLocale: readUserPreferredLocaleV0(),
      tenantDefaultLocale: tenantDefaultLocaleFromServer ?? readTenantDefaultLocaleV0(tenantId),
      browserLocale: readBrowserLocaleV0(),
    });
  }, [plane, tenantId, tenantDefaultLocaleFromServer, initialLocale, sessionTick]);

  const locale = resolveSafeLocale(resolved.locale);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const meta = LOCALE_METADATA_V0[locale];
    document.documentElement.lang = meta.htmlLang;
    document.documentElement.dir = meta.direction;
  }, [locale]);

  const setSessionLocale = useCallback((next: SupportedLocale) => {
    writeSessionLocaleV0(resolveSafeLocale(next));
    setSessionTick((n) => n + 1);
  }, []);

  const setUserPreferredLocale = useCallback((next: SupportedLocale) => {
    writeUserPreferredLocaleV0(resolveSafeLocale(next));
    setSessionTick((n) => n + 1);
  }, []);

  const setTenantDefaultLocale = useCallback(
    (next: SupportedLocale) => {
      if (!tenantId) return;
      writeTenantDefaultLocaleV0(tenantId, resolveSafeLocale(next));
      setSessionTick((n) => n + 1);
    },
    [tenantId]
  );

  const t = useCallback(
    (key: RequiredTranslationKeyV0, params?: TranslationParamsV0) => tV0(key, locale, params),
    [locale]
  );

  const value = useMemo<LocaleContextValueV0>(
    () => ({
      locale,
      plane,
      source: resolved.source,
      setSessionLocale,
      setUserPreferredLocale,
      setTenantDefaultLocale,
      t,
      htmlLang: LOCALE_METADATA_V0[locale].htmlLang,
      direction: LOCALE_METADATA_V0[locale].direction,
    }),
    [
      locale,
      plane,
      resolved.source,
      setSessionLocale,
      setUserPreferredLocale,
      setTenantDefaultLocale,
      t,
    ]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocaleV0(): LocaleContextValueV0 {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error('useLocaleV0 must be used within LocaleProvider');
  }
  return ctx;
}

/** Safe hook for optional contexts — falls back to en without crashing. */
export function useLocaleOptionalV0(): LocaleContextValueV0 {
  const ctx = useContext(LocaleContext);
  if (ctx) return ctx;
  return {
    locale: 'en',
    plane: 'business',
    source: 'fallback_en',
    setSessionLocale: () => undefined,
    setUserPreferredLocale: () => undefined,
    setTenantDefaultLocale: () => undefined,
    t: (key, params) => tV0(key, 'en', params),
    htmlLang: 'en',
    direction: 'ltr',
  };
}
